import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap, Polyline } from 'react-leaflet';
import { Box, Typography, Divider, Chip } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import { renderToStaticMarkup } from 'react-dom/server';
import { Zap } from 'lucide-react';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";
import CommonTable from "../../../common/CommonTable";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
});

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const findNameFromCode = (nameMap, code) => nameMap[code] || code;

const SectionLabel = ({ text }) => (
    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
        {text}
    </Typography>
);

const getPopupContent = (event, municipalityNames, subMunicipalityNames, sucoNames) => {
    const municipalityName = findNameFromCode(municipalityNames, event.municipality);
    const subMunicipalityName = findNameFromCode(subMunicipalityNames, event.sub_municipality);
    const sucoName = findNameFromCode(sucoNames, event.suco);

    return `
        <strong>Lightning Report</strong><br/>
        <hr style="margin: 4px 0;"/>
        <strong>Location:</strong> ${event.location}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Date of Event:</strong> ${event.date_lightning}<br/>
        <strong>Time of Event:</strong> ${event.time_lightning}<br/>
        <strong>Time Type:</strong> ${event.time_type}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Effect Type:</strong> ${event.effect_type}<br/>
        <strong>Strike Type:</strong> ${event.strike || '-'}<br/>
        <strong>Damage Description:</strong> ${event.damage_description}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Affected Status:</strong> ${event.affected_status}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Municipality:</strong> ${municipalityName}<br/>
        <strong>Sub-Municipality:</strong> ${subMunicipalityName}<br/>
        <strong>Suco:</strong> ${sucoName}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Additional Info:</strong> ${event.additional_info}<br/>
    `;
};

const createLightningIcon = (color = '#eab308', isSelected = false) => {
    const iconSvg = renderToStaticMarkup(<Zap size={12} stroke="white" strokeWidth={2} />)
        .replace(/<svg/, `<svg width="12" height="12" viewBox="0 0 24 24"`);

    return L.divIcon({
        className: 'custom-div-icon',
        html: `
            <div style="position: relative; width: 20px; height: 20px;">
                <div style="
                    width: 20px; height: 20px; background-color: ${color}; border-radius: 50%;
                    position: absolute; top: -10px; left: -10px; z-index: 1;
                    display: flex; justify-content: center; align-items: center;
                    border: ${isSelected ? '2px solid white' : 'none'};
                    box-shadow: ${isSelected ? '0 0 8px rgba(0,0,0,0.5)' : 'none'};
                ">${iconSvg}</div>
                <div style="
                    width: 20px; height: 20px; background-color: ${color}; border-radius: 50%;
                    position: absolute; top: -10px; left: -10px; opacity: 0.4;
                    animation: pulse-ring 1.5s infinite; z-index: 0;
                "></div>
            </div>
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
    });
};

const MapFlyToSelectedMarker = ({ selectedEvent, events, adm1Data, setSelectedEvent, municipalityNames, subMunicipalityNames, sucoNames }) => {
    const map = useMap();

    useEffect(() => {
        if (adm1Data) {
            const geojsonLayer = L.geoJSON(adm1Data);
            map.fitBounds(geojsonLayer.getBounds(), { padding: [50, 50] });
        }
    }, [adm1Data, map]);

    useEffect(() => {
        if (selectedEvent) {
            let bounds = null;
            if (selectedEvent.geojson_feature) {
                bounds = L.geoJSON(selectedEvent.geojson_feature).getBounds();
            } else if (selectedEvent.starting_point_lat) {
                bounds = L.latLngBounds([
                    [selectedEvent.starting_point_lat, selectedEvent.starting_point_long],
                    [selectedEvent.ending_point_lat, selectedEvent.ending_point_long]
                ]);
            } else if (selectedEvent.latitude && selectedEvent.longitude) {
                map.flyTo([selectedEvent.latitude, selectedEvent.longitude], 12, { animate: true, duration: 1.5 });
            }
            if (bounds) map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [selectedEvent, map]);

    const popupOptions = { maxHeight: 280, className: 'custom-leaflet-popup' };

    return (
        <>
            {adm1Data && (
                <GeoJSON data={adm1Data} style={() => ({ color: '#2563eb', fillOpacity: 0, weight: 2, opacity: 0.6 })} />
            )}
            {events && events.map((event, idx) => {
                const isSelected = selectedEvent?.report_id === event.report_id;
                const icon = createLightningIcon(undefined, isSelected);
                const popupContent = getPopupContent(event, municipalityNames, subMunicipalityNames, sucoNames);

                if (event.geojson_feature) {
                    return (
                        <GeoJSON
                            key={idx}
                            data={event.geojson_feature}
                            style={() => ({
                                color: isSelected ? '#a16207' : '#eab308',
                                weight: isSelected ? 5 : 3,
                                opacity: isSelected ? 0.9 : 0.7,
                                fillColor: isSelected ? '#facc15' : '#fde047',
                                fillOpacity: isSelected ? 0.3 : 0.2
                            })}
                            onEachFeature={(feature, layer) => {
                                layer.bindPopup(popupContent, popupOptions);
                                layer.on('click', () => {
                                    setSelectedEvent(event);
                                    map.fitBounds(layer.getBounds(), { padding: [50, 50] });
                                });
                                if (isSelected) layer.openPopup();
                            }}
                        />
                    );
                } else if (event.starting_point_lat && event.starting_point_long) {
                    const latlngs = [
                        [event.starting_point_lat, event.starting_point_long],
                        [event.ending_point_lat, event.ending_point_long]
                    ];
                    return (
                        <React.Fragment key={idx}>
                            <Polyline positions={latlngs} color={isSelected ? '#a16207' : '#eab308'} weight={isSelected ? 5 : 3} opacity={isSelected ? 0.9 : 0.7} />
                            {latlngs.map((pos, i) => (
                                <Marker key={i} position={pos} icon={icon} eventHandlers={{ click: () => setSelectedEvent(event) }}>
                                    <Popup {...popupOptions}><div dangerouslySetInnerHTML={{ __html: popupContent }} /></Popup>
                                </Marker>
                            ))}
                        </React.Fragment>
                    );
                } else if (event.latitude && event.longitude) {
                    return (
                        <Marker key={idx} position={[event.latitude, event.longitude]} icon={icon} eventHandlers={{ click: () => setSelectedEvent(event) }}>
                            <Popup {...popupOptions}><div dangerouslySetInnerHTML={{ __html: popupContent }} /></Popup>
                        </Marker>
                    );
                }
                return null;
            })}
        </>
    );
};

function LightningFeltReportsList() {
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [adm1Data, setAdm1Data] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [municipalityNames, setMunicipalityNames] = useState({});
    const [subMunicipalityNames, setSubMunicipalityNames] = useState({});
    const [sucoNames, setSucoNames] = useState({});

    useEffect(() => {
        if (!document.getElementById('lightning-reports-pulse-animation')) {
            const style = document.createElement('style');
            style.id = 'lightning-reports-pulse-animation';
            style.innerHTML = `
                @keyframes pulse-ring {
                    0% { transform: scale(0.5); opacity: 0.6; }
                    80%, 100% { transform: scale(2); opacity: 0; }
                }
                .custom-leaflet-popup .leaflet-popup-content-wrapper {
                    border-radius: 8px; padding: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }
                .custom-leaflet-popup .leaflet-popup-content {
                    font-size: 13px; max-height: 280px; overflow-y: auto;
                    padding-right: 8px; width: auto; max-width: none;
                    color: #374151; line-height: 1.5;
                }
                .custom-leaflet-popup .leaflet-popup-content strong { color: #1e293b; font-weight: 600; }
                .custom-leaflet-popup .leaflet-popup-content hr { border: none; border-top: 1px solid #e2e8f0; margin: 6px 0; }
            `;
            document.head.appendChild(style);
        }
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [geojson1, muniRes, subMuniRes, sucoRes, reportsRes] = await Promise.all([
                    axios.get('/DATA/OSMbased_adm1_boundary.json'),
                    axios.post(`${backendUrl}Municipalities/municipalities_list/`),
                    axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
                    axios.post(`${backendUrl}Municipalities/suco_list/`),
                    axios.get(`${backendUrl}lightning/lightning-felt-reports/`)
                ]);
                setAdm1Data(geojson1.data);
                setMunicipalityNames(muniRes.data.reduce((acc, c) => ({ ...acc, [c.adm1_code]: c.adm1 }), {}));
                setSubMunicipalityNames(subMuniRes.data.reduce((acc, c) => ({ ...acc, [c.adm2_code]: c.adm2 }), {}));
                setSucoNames(sucoRes.data.reduce((acc, c) => ({ ...acc, [c.adm3_code]: c.adm3 }), {}));
                setEvents(reportsRes.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false);
            }
        };
        fetchAllData();
    }, []);

    const tableRows = useMemo(() => events.map((event) => ({
        ...event,
        municipalityName: findNameFromCode(municipalityNames, event.municipality),
        subMunicipalityName: findNameFromCode(subMunicipalityNames, event.sub_municipality),
        sucoName: findNameFromCode(sucoNames, event.suco),
        locationDataType: event.geojson_feature ? 'Polygon' : event.starting_point_lat ? 'Two-Point Line' : 'Single Point',
    })), [events, municipalityNames, subMunicipalityNames, sucoNames]);

    const columns = useMemo(() => [
        { key: 'date_lightning', label: 'Date', sortable: true },
        { key: 'time_lightning', label: 'Time' },
        { key: 'municipalityName', label: 'Municipality', sortable: true },
        { key: 'subMunicipalityName', label: 'Sub Municipality', sortable: true },
        { key: 'sucoName', label: 'Suco', sortable: true },
        { key: 'locationDataType', label: 'Location Data Type' },
        { key: 'location', label: 'Location' },
        { key: 'time_type', label: 'Time Type' },
        { key: 'effect_type', label: 'Effect Type', sortable: true },
        { key: 'strike', label: 'Strike Type', sortable: true },
        { key: 'damage_description', label: 'Damage Description' },
        { key: 'affected_status', label: 'Affected Status' },
        { key: 'advisory', label: 'Advisory', sortable: true },
        {
            key: 'image',
            label: 'Image',
            render: (row) => row.image
                ? <a href={`${backendUrl}${row.image}`} target="_blank" rel="noopener noreferrer">View</a>
                : '-',
        },
        { key: 'additional_info', label: 'Additional Info' },
    ], []);

    return (
        <Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <Typography sx={{ fontSize: '15px', color: '#64748b' }}>Loading lightning reports...</Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
                                Lightning Felt Report
                            </Typography>
                            <Chip
                                label={`${events.length} ${events.length === 1 ? 'Report' : 'Reports'}`}
                                size="small"
                                sx={{ fontSize: '12px', fontWeight: 600, background: '#2563eb', color: '#fff', height: '22px' }}
                            />
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                            View and explore Lightning reports on map and table
                        </Typography>
                    </Box>

                    {events.length > 0 && (
                        <>
                            <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', mb: 3 }}>
                                <MapContainer center={[-8.8742, 125.7275]} zoom={8} style={{ height: '450px', width: '100%' }}>
                                  <MapHomeControl />
                                  <MapInfoControl text="Locations of submitted lightning reports. Click a marker to view report details." />
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    <MapFlyToSelectedMarker
                                        selectedEvent={selectedEvent}
                                        events={events}
                                        adm1Data={adm1Data}
                                        setSelectedEvent={setSelectedEvent}
                                        municipalityNames={municipalityNames}
                                        subMunicipalityNames={subMunicipalityNames}
                                        sucoNames={sucoNames}
                                    />
                                </MapContainer>
                            </Box>
                            <Divider sx={{ borderColor: '#e2e8f0', mb: 3 }} />
                        </>
                    )}

                    <SectionLabel text="Reports List" />
                    {events.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, px: 3, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontSize: '15px', color: '#64748b', mb: 1 }}>No lightning reports available</Typography>
                            <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>Reports will appear here once data is submitted</Typography>
                        </Box>
                    ) : (
                        <CommonTable
                            tableData={tableRows}
                            columns={columns}
                            searchableKeys={['municipalityName', 'subMunicipalityName', 'sucoName', 'location', 'effect_type', 'strike', 'additional_info']}
                            tableName="Lightning_Felt_Reports"
                            emptyMessage="No lightning reports found."
                            rowIdKey="report_id"
                            highlightRowKey={selectedEvent?.report_id ?? null}
                            onRowClick={(row) => setSelectedEvent(row)}
                            defaultSort={{ key: 'date_lightning', direction: 'desc' }}
                        />
                    )}
                </>
            )}
        </Box>
    );
}

export default LightningFeltReportsList;
